import { useMemo, useState } from 'react';
import { useCRMFunction } from './useCRMFunction';
import { ChangeAlertCallbackType } from '../../../Root';
import { FormGroup, FormControlLabel, Switch, Typography, Tooltip, IconButton } from '@mui/material';
import { useDialog } from "@albo-ui/core";
import { sellerRole, sellerType } from '../../../../util/DataObject';
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import moment from 'moment';
moment.locale('es');


export const useCRMSellers = (changeAlert: ChangeAlertCallbackType) => {
    const { onHandleGetData, onHandleSellerCreate, onHandleSellerUpdate } = useCRMFunction(changeAlert);
    const agent = localStorage.session ? JSON.parse(localStorage.session) : {};

    const dialogProps = useDialog();
    const dialogConfirm = useDialog();
    const [dialogText, setDialogText] = useState({});
    const [row, setRow] = useState(null);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({ filters: {} });

    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [totalDocuments, setTotalDocuments] = useState(0);

    const saveData = async (result) => {
        let validate = Array.isArray(result?.content);
        setData(JSON.parse(JSON.stringify(validate ? result.content || [] : [result.content])));;
        setTotalDocuments(result.pagination ? result.pagination['total_documents'] : 0);
    };

    const getData = async (newPage = page, newPerPage = rowsPerPage, form) => {
        setLoading(true);
        let searchType = form['filters']['keyword'] ? 'Find' : 'List'
        let service = `sellerCrm${searchType}`;

        let f = {};
        if(searchType === 'List'){
            f = form;
            f['pagination'] = {page_number: newPage, results_per_page: newPerPage};
            setForm(JSON.parse(JSON.stringify(f)));
        } else {
            f = form
            f['pagination'] = {page_number: newPage, results_per_page: newPerPage};
            form['filters']['status'] && (f['filters']['status'] = form['filters']['status'])

            setForm(JSON.parse(JSON.stringify(f)));
        }

        let resp = await onHandleGetData(f, service);
        resp ? saveData(resp) : saveData({ content: [] });
        setLoading(false);
    };

    const onHandleFind = async (payload) => {
        getData(1, rowsPerPage, payload);
    };

    const onHandleChange = (key1, key2, e) => {
        let f = form;
        let value = e.target.value;

        if(key1 === 'email'){;
            value = value.trim();
            f = {filters: {
                keyword: value,
                type: key1,
            }}
        } else if(key1 && value){
            f['filters'][key1] = value;
        } else if(key1 && !value){
            delete f['filters'][key1];
        }

        setForm(JSON.parse(JSON.stringify(f)));

        return key2 ? onHandleFind(f) : null
    };

    const changePage = (action, page, rows= rowsPerPage) => {
		setPage(page+1);
		getData(page+1, rows,  form);
	};
	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setRowsPerPage(JSON.parse(JSON.stringify(event.target.value)));
        changePage(null, 0, parseInt(event.target.value, 10));
	};

    const clearInput = () => {
        let f = form;
        delete form["filters"]["keyword"];
        delete form["filters"]["type"];
        setForm(JSON.parse(JSON.stringify(f)));
    };

    const onHandleStatus = async (status, uuid) => {
        setLoading(true);

        let payload = {
            uuid: uuid,
            status: status === "enabled" ? "disabled" : "enabled",
            updated_by: agent["uuid"],
        };

        let resp = await onHandleSellerUpdate(payload);
        resp ? getData(page, rowsPerPage, form) : setLoading(false);
    };

    const onHandleDelete = async () => {
        setLoading(true);
        dialogConfirm.onClose();

        let payload = {
            uuid: row.uuid,
            status: "disabled",
            active: false,
            updated_by: agent["uuid"],
        };

        let resp = await onHandleSellerUpdate(payload);
        resp ? getData(page, rowsPerPage, form) : setLoading(false);

        setRow(null);
    };

    const onHandleCreate = async (payload) => {
        setLoading(true);
        dialogProps.onClose();

        let request = payload;
        request["created_by"] = agent["uuid"];
        request["status"] = "enabled";

        let resp = await onHandleSellerCreate(request);
        resp ? getData(page, rowsPerPage, form) : setLoading(false);

        setRow(null);
    };

    const onHandleUpdate = async (payload) => {
        setLoading(true);
        dialogProps.onClose();

        let request = payload;
        request["updated_by"] = agent["uuid"];

        let resp = await onHandleSellerUpdate(request);
        resp ? getData(page, rowsPerPage, form) : setLoading(false);

        setRow(null);
    };

    const onConfirmDelete = (row) => {
        setRow(row);
        setDialogText({
            title: "Eliminar registro",
            message: `¿Está seguro que desea eliminar el siguiente ${sellerRole.map(
                (r) => (r.key === row["role"] ? r.label : null)
            )}?`,
        });
        dialogConfirm.openDialog();
    };

    const onHandleForm = (row = null) => {
        setRow(row);
        dialogProps.openDialog();
    };

    const calculateColumns = useMemo(() => {
        const columns = [
            { selector: (row) => row['name'], name: 'Nombre', wrap: true, sortable: true, width: '20%' },
            { selector: (row) => row['general']['email'], name: 'Email', wrap: true, sortable: true, width: '20%' },
            { selector: (row) => row['general']['phone'], name: 'Teléfono', wrap: true, sortable: true },
            { cell: (row) => {
                return <FormGroup>
                    <FormControlLabel
                        control={
                            <Switch
                                defaultChecked={row.status === 'enabled' ? true : false}
                                onChange={onHandleStatus.bind(this, row.status, row.uuid)}
                            />
                        }
                        label={row.status === 'enabled' ? 'Activo' :  'Inactivo'}
                    />
                </FormGroup>
            }, name: 'Estatus', width: '15%'
            },
            { selector: (row) => sellerRole.map((r) => r.key === row['role'] ? r.label : null), name: 'Rol', wrap: true, sortable: true, width: '10%' },
            { selector: (row) => row['type'] ? sellerType.map((r) => r.key === row['type'] ? r.label : null) : 'Por asignar', name: 'Tipo', wrap: true, sortable: true, width: '10%' },
            {
                cell: (row) => {
                    return <Typography fontWeight="400" fontFamily="Muli,sans-serif" color="#000000de" fontSize="0.875rem">
                        <Tooltip title='Editar'>
                            <IconButton onClick={onHandleForm.bind(this, row)}>
                                <EditIcon/>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title='Borrar'>
                            <IconButton onClick={onConfirmDelete.bind(this, row)}>
                                <DeleteIcon/>
                            </IconButton>
                        </Tooltip>
                    </Typography>
                }, name: 'Acciones', width: '10%'
            }
        ];

        return columns
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return {
        data,
        form,
        loading,
        dialogText,
        dialogConfirm,
        dialogProps,
        row,
        columns: calculateColumns,
        pagination: { page, rowsPerPage, totalDocuments},
        getData,
        saveData,
        onHandleChange,
        onHandleFind,
        onHandleDelete,
        onHandleUpdate,
        onHandleForm,
        onHandleCreate,
        setRow,
        clearInput,
        changePage,
        handleChangeRowsPerPage
    };
};
